import { List, Record } from "immutable";
import { type TTicketDuration } from "utils/durationsOptions";
import { IsNullError } from "utils/errors/isNullError";
import { removeRedundantSpaces } from "utils/strings";

import { ApprovalAlgorithmModel } from "./ApprovalAlgorithmModel";
import { BundleItemModel, type IBundleItemSchema } from "./BundleItemModel";

const defaults = {
  allowedDurations: null as List<TTicketDuration> | null,
  approvalAlgorithm: null as ApprovalAlgorithmModel | null,
  bundleItems: null as List<BundleItemModel> | null,
  category: null as null | string,
  createdAt: new Date(0),
  creatorApiTokenId: null as null | string,
  creatorId: null as null | string,
  description: null as null | string,
  eligible: false,
  id: "",
  isDeleted: false,
  name: "",
  tags: null as null | string[],
};

interface IBundleSchema {
  allowedDurations: List<TTicketDuration> | null;
  approvalAlgorithmId: string;
  bundleItems?: List<IBundleItemSchema> | null;
  category: null | string;
  createdAt: Date | null;
  description: null | string;
  id?: null | string;
  name: string;
  tags: null | string[];
}

export class BundleModel extends Record<typeof defaults>(defaults) {
  static fromServerData(data: unknown) {
    const {
      allowedDurations,
      approvalAlgorithm,
      bundleItems,
      category,
      createdAt,
      creatorApiTokenId,
      creatorId,
      description,
      eligible,
      id,
      isDeleted,
      name,
      tags,
    } = data as typeof defaults;
    return new BundleModel({
      allowedDurations: allowedDurations ? List(allowedDurations) : null,
      approvalAlgorithm: approvalAlgorithm ? ApprovalAlgorithmModel.fromServerData(approvalAlgorithm) : null,
      bundleItems: bundleItems ? List(bundleItems.map((bundleItem) => BundleItemModel.fromServerData(bundleItem))) : null,
      category,
      createdAt: createdAt && new Date(createdAt),
      creatorApiTokenId,
      creatorId,
      description,
      eligible,
      id,
      isDeleted,
      name,
      tags,
    });
  }

  toDropDownOption() {
    return {
      label: this.name,
      value: this.id,
    };
  }

  toServerData(): IBundleSchema {
    const { allowedDurations, approvalAlgorithm, bundleItems, category, createdAt, description, id, name, tags } = this;
    if (!approvalAlgorithm) {
      throw IsNullError.from({
        location: "BundleModel.toServerData",
        parentObject: {
          name: "this (BundleModel)",
          value: this,
        },
        requestedProperty: "approvalAlgorithm",
      });
    }
    return {
      allowedDurations,
      approvalAlgorithmId: approvalAlgorithm && approvalAlgorithm.id,
      bundleItems: bundleItems ? bundleItems.map((bundleItem) => bundleItem.toServerData()) : null,
      category,
      createdAt,
      description,
      id,
      name: removeRedundantSpaces(name),
      tags,
    };
  }
}
